import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import {
  bundleChannelSubscription,
  bundleEventName,
  orderCancelEvent,
  orderChannelSubscription,
  orderCreateEvent,
  orderUpdateEvent,
} from '../../../const/pusherChannelEventConsts';
import {
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../../utils/pusher';

const throttledOrdersUpdated = _.throttle(
  async (
    pusherEvent: string,
    clearIdsToUpdate: (
      orderIds: string[],
      action: 'update' | 'cancel' | 'create'
    ) => void,
    ordersToMutate: { [key: string]: string[] },
    updateDataStructure: (
      orderIds: string[],
      pusherEvent: string
    ) => Promise<void>
  ) => {
    const currentBatchUpdateOrders = [...ordersToMutate.update];
    clearIdsToUpdate(currentBatchUpdateOrders, 'update');
    await updateDataStructure(currentBatchUpdateOrders, pusherEvent);
  },
  1500,
  { leading: false }
);

const throttledOrdersCanceled = _.throttle(
  async (
    pusherEvent: string,
    clearIdsToUpdate: (
      orderIds: string[],
      action: 'update' | 'cancel' | 'create'
    ) => void,
    ordersToMutate: { [key: string]: string[] },
    updateDataStructure: (
      orderIds: string[],
      pusherEvent: string
    ) => Promise<void>
  ) => {
    const currentBatchCancelOrders = [...ordersToMutate.cancel];
    clearIdsToUpdate(currentBatchCancelOrders, 'cancel');
    await updateDataStructure(currentBatchCancelOrders, pusherEvent);
  },
  1500,
  { leading: false }
);

const throttledOrdersCreated = _.throttle(
  async (
    pusherEvent: string,
    clearIdsToUpdate: (
      orderIds: string[],
      action: 'update' | 'cancel' | 'create'
    ) => void,
    ordersToMutate: { [key: string]: string[] },
    updateDataStructure: (
      orderIds: string[],
      pusherEvent: string
    ) => Promise<void>
  ) => {
    const currentBatchCreateOrders = [...ordersToMutate.create];
    clearIdsToUpdate(currentBatchCreateOrders, 'create');
    await updateDataStructure(currentBatchCreateOrders, pusherEvent);
  },
  1500,
  { leading: false }
);

export function useSubscribeToPlannerData(
  updateDataStructure: (orderIds: string[], event: string) => Promise<void>
) {
  const subscribeToPusherForPlannerData = useCallback(() => {
    const ordersToMutate: { [key: string]: string[] } = {
      update: [],
      create: [],
      cancel: [],
    };

    const clearIds = (
      updatedIds: string[],
      action: 'update' | 'cancel' | 'create'
    ) => {
      if (ordersToMutate[action].length === updatedIds.length) {
        ordersToMutate[action] = [];
      } else {
        ordersToMutate[action] = ordersToMutate[action].filter(
          id => !updatedIds.includes(id)
        );
      }
    };

    subscribeToChannel(
      orderChannelSubscription,
      orderCreateEvent,
      async pusherData => {
        const { orderIds }: { orderIds: string[]; message: string } =
          pusherData;
        ordersToMutate.create.push(...orderIds);

        throttledOrdersCreated(
          orderCreateEvent,
          clearIds,
          ordersToMutate,
          updateDataStructure
        );
      }
    );

    subscribeToChannel(
      orderChannelSubscription,
      orderCancelEvent,
      async pusherData => {
        const { orderIds }: { orderIds: string[]; message: string } =
          pusherData;
        ordersToMutate.cancel.push(...orderIds);
        throttledOrdersCanceled(
          orderCancelEvent,
          clearIds,
          ordersToMutate,
          updateDataStructure
        );
      }
    );

    subscribeToChannel(
      orderChannelSubscription,
      orderUpdateEvent,
      async pusherData => {
        const { orderIds }: { orderIds: string[]; message: string } =
          pusherData;
        ordersToMutate.update.push(...orderIds);
        throttledOrdersUpdated(
          orderUpdateEvent,
          clearIds,
          ordersToMutate,
          updateDataStructure
        );
      }
    );

    /* This part is for detecting if the bundle changed */
    subscribeToChannel(bundleChannelSubscription, bundleEventName, async () => {
      updateDataStructure([], bundleEventName);
    });
  }, [updateDataStructure]);

  useEffect(() => {
    subscribeToPusherForPlannerData();
    return () => {
      unsubscribeFromChannel(orderChannelSubscription);
      unsubscribeFromChannel(bundleChannelSubscription);
    };
  }, [subscribeToPusherForPlannerData]);
}
